import { useEffect, useState, useCallback } from "react";
import { useAba } from "@/lib/useAba";
import { Megaphone, RotateCcw, X, Check, Monitor, Settings2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Field, StatCard } from "@/components/common";
import { SellBlocked } from "@/components/TrialBanner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime, todayISO, isManagerRole } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const tenantBody = (t, settings) => ({ razao_social: t.razao_social, nome_fantasia: t.nome_fantasia, telefone: t.telefone, endereco: t.endereco, segmento: t.segmento, settings });

function QueuesDialog({ open, onOpenChange }) {
  const { session, refresh } = useAuth();
  const [qs, setQs] = useState(session.tenant.settings.ticket_queues);
  const upd = (i, patch) => setQs((l) => l.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const save = async () => {
    try {
      await api.put("/settings", tenantBody(session.tenant, { ticket_queues: qs.filter((q) => q.name && q.prefix).map((q) => ({ ...q, id: q.id || q.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") })) }));
      await refresh();
      toast.success("Filas salvas");
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="queues-modal">
        <DialogHeader><DialogTitle>Filas de atendimento</DialogTitle></DialogHeader>
        {qs.map((q, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={q.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Nome" data-testid={`queue-name-${i}`} />
            <Input value={q.prefix} maxLength={2} onChange={(e) => upd(i, { prefix: e.target.value.toUpperCase() })} className="w-16" placeholder="Letra" data-testid={`queue-prefix-${i}`} />
            <label className="flex items-center gap-1 text-xs"><Checkbox checked={q.priority} onCheckedChange={(v) => upd(i, { priority: !!v })} />Prior.</label>
            <Button size="icon" variant="ghost" onClick={() => setQs((l) => l.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button variant="outline" onClick={() => setQs((l) => [...l, { id: "", name: "", prefix: "", priority: false }])} data-testid="add-queue-btn"><Plus className="h-4 w-4 mr-1" />Nova fila</Button>
        <DialogFooter><Button onClick={save} data-testid="save-queues-btn">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Report() {
  const [range, setRange] = useState({ start: todayISO(6), end: todayISO() });
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/tickets/report?start=${range.start}&end=${range.end}`).then((x) => setR(x.data)); }, [range]);
  return (
    <div className="space-y-4" data-testid="tickets-report">
      <div className="flex gap-2">
        <Input type="date" value={range.start} onChange={(e) => setRange((x) => ({ ...x, start: e.target.value }))} className="w-44" data-testid="tickets-report-start" />
        <Input type="date" value={range.end} onChange={(e) => setRange((x) => ({ ...x, end: e.target.value }))} className="w-44" data-testid="tickets-report-end" />
      </div>
      {r && (<>
        <div className="grid grid-cols-2 gap-4"><StatCard label="Senhas emitidas" value={r.total} /><StatCard label="Espera média" value={`${r.avg_wait} min`} tone="warning" testId="tickets-avg-wait" /></div>
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="text-left text-muted-foreground"><th className="p-3">Fila</th><th>Total</th><th>Atendidas</th><th>Canceladas</th><th>Espera média</th><th>Atendimento médio</th></tr></thead>
            <tbody>{r.queues.map((q) => <tr key={q.queue} className="border-t"><td className="p-3">{q.queue}</td><td>{q.total}</td><td>{q.done}</td><td>{q.cancelled}</td><td>{q.avg_wait} min</td><td>{q.avg_service} min</td></tr>)}</tbody></table>
        </div>
        <div className="card-surface p-4 text-sm"><p className="eyebrow mb-2">Chamadas por atendente</p>{r.attendants.map((a) => <p key={a.name}>{a.name}: <b>{a.calls}</b></p>)}</div>
        <div className="card-surface overflow-x-auto" data-testid="tickets-history">
          <table className="w-full text-sm"><thead><tr className="text-left text-muted-foreground"><th className="p-3">Senha</th><th>Fila</th><th>Emitida</th><th>Chamada</th><th>Atendente</th><th>Status</th></tr></thead>
            <tbody>{r.history.map((t) => <tr key={t.id} className="border-t"><td className="p-3 font-mono font-bold">{t.code}</td><td>{t.queue_name}</td><td>{fmtTime(t.created_at)}</td><td>{fmtTime(t.called_at)}</td><td>{t.called_by || "—"}{t.counter ? ` (${t.counter})` : ""}</td><td>{{ waiting: "Aguardando", called: "Chamada", done: "Atendida", cancelled: "Cancelada" }[t.status]}</td></tr>)}</tbody></table>
        </div>
      </>)}
    </div>
  );
}

export default function TicketsPage() {
  const aba = useAba("atendimento");
  const { session } = useAuth();
  const queues = session.tenant.settings.ticket_queues;
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [last, setLast] = useState(null);
  const [filter, setFilter] = useState("");
  const [counter, setCounter] = useState(() => localStorage.getItem("pdv_counter") || "Guichê 1");
  const [cfg, setCfg] = useState(false);
  const load = useCallback(() => api.get("/tickets").then((r) => setList(r.data)), []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const run = async (fn, msg) => { try { const r = await fn(); if (msg) toast.success(typeof msg === "function" ? msg(r.data) : msg); load(); return r; } catch (e) { toast.error(errMsg(e)); } };
  const emit = (queue) => run(() => api.post("/tickets", { queue, customer_name: name }), (d) => { setLast(d); setName(""); return `Senha ${d.code} emitida`; });
  const waiting = list.filter((t) => t.status === "waiting" && (!filter || t.queue === filter)).sort((a, b) => b.priority - a.priority || a.created_at.localeCompare(b.created_at));
  const called = list.filter((t) => t.status === "called").sort((a, b) => (b.last_call_at || "").localeCompare(a.last_call_at || ""));
  const current = called[0];

  return (
    <>
      <PageHeader eyebrow="Fila de atendimento" title="Tickets" subtitle={`${list.filter((t) => t.status === "waiting").length} aguardando · ${list.filter((t) => t.status === "done").length} atendidos hoje`}
        actions={<>
          <Button asChild variant="outline" data-testid="open-public-panel-btn"><a href={`/painel/${session.tenant.menu_slug}`} target="_blank" rel="noreferrer"><Monitor className="h-4 w-4 mr-1" />Painel público</a></Button>
          {isManagerRole(session.user) && <Button variant="outline" onClick={() => setCfg(true)} data-testid="config-queues-btn"><Settings2 className="h-4 w-4 mr-1" />Filas</Button>}
        </>} />
      <SellBlocked />
      <Tabs key={aba} defaultValue={aba}>
        <TabsList><TabsTrigger value="atendimento" data-testid="tickets-tab-atendimento">Atendimento</TabsTrigger><TabsTrigger value="relatorio" data-testid="tickets-tab-relatorio">Histórico e relatório</TabsTrigger></TabsList>
        <TabsContent value="atendimento" className="mt-4 space-y-4">
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="card-surface p-5 space-y-3">
              <p className="eyebrow">Gerar senha</p>
              <Field label="Nome (opcional)"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ticket-name-input" /></Field>
              <div className="grid grid-cols-2 gap-2">
                {queues.map((q) => (
                  <Button key={q.id} className={cn("h-14 whitespace-normal text-xs leading-tight", q.priority && "bg-amber-500 hover:bg-amber-600 text-white")} onClick={() => emit(q.id)} data-testid={`ticket-new-${q.id}`}>
                    <span className="font-mono mr-1">{q.prefix}</span>{q.name}
                  </Button>
                ))}
              </div>
              {last && <div className="rounded-xl border-2 border-dashed p-3 text-center" data-testid="last-ticket-issued"><p className="eyebrow">{last.queue_name}</p><p className="font-mono text-4xl font-bold">{last.code}</p></div>}
            </div>
            <div className="card-surface p-6 flex flex-col items-center justify-center gap-3 text-center bg-primary/5 border-primary/30">
              <p className="eyebrow">Senha atual</p>
              <p className="font-mono text-7xl font-bold text-primary" data-testid="current-ticket-code">{current?.code || "---"}</p>
              <p className="text-sm text-muted-foreground">{current ? `${current.queue_name} · ${current.counter || ""} · ${current.called_by}` : "\u00a0"}</p>
              <div className="grid grid-cols-2 gap-2 w-full">
                <Input value={counter} onChange={(e) => { setCounter(e.target.value); localStorage.setItem("pdv_counter", e.target.value); }} placeholder="Guichê" data-testid="ticket-counter-input" />
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-md border bg-transparent px-2 text-sm" data-testid="ticket-queue-filter">
                  <option value="">Todas as filas</option>{queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                </select>
              </div>
              <Button size="lg" className="h-14 w-full text-lg" onClick={() => run(() => api.post("/tickets/call-next", { queue: filter || null, counter }), (d) => `Chamando ${d.code}`)} disabled={!waiting.length} data-testid="ticket-call-next-btn"><Megaphone className="h-5 w-5 mr-2" />Chamar próxima</Button>
            </div>
            <div className="card-surface">
              <p className="border-b p-4 eyebrow">Aguardando ({waiting.length})</p>
              <ul className="divide-y max-h-80 overflow-y-auto" data-testid="tickets-waiting-list">
                {waiting.map((t) => (
                  <li key={t.id} className="flex items-center gap-3 p-3" data-testid={`ticket-row-${t.code}`}>
                    <span className={cn("font-mono text-lg font-bold w-16", t.priority ? "text-amber-500" : "text-primary")}>{t.code}</span>
                    <span className="flex-1 text-sm truncate">{t.queue_name}{t.customer_name && ` · ${t.customer_name}`}<span className="block text-xs text-muted-foreground">{fmtTime(t.created_at)}</span></span>
                    <Button size="icon" variant="ghost" onClick={() => run(() => api.patch(`/tickets/${t.id}`, { status: "cancelled" }), "Senha cancelada")} data-testid={`ticket-cancel-${t.code}`}><X className="h-4 w-4" /></Button>
                  </li>
                ))}
                {!waiting.length && <li className="p-6 text-center text-sm text-muted-foreground">Nenhuma senha aguardando.</li>}
              </ul>
            </div>
          </div>
          <div className="card-surface">
            <p className="border-b p-4 eyebrow">Em atendimento</p>
            <ul className="divide-y" data-testid="tickets-called-list">
              {called.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center gap-3 p-3">
                  <span className="font-mono text-lg font-bold w-16">{t.code}</span>
                  <span className="flex-1 text-sm">{t.queue_name} · {t.counter} · {t.called_by} · {t.calls}x chamada</span>
                  <Button size="sm" variant="outline" onClick={() => run(() => api.post(`/tickets/${t.id}/recall`, { counter }), `Rechamando ${t.code}`)} data-testid={`ticket-recall-${t.code}`}><RotateCcw className="h-4 w-4 mr-1" />Rechamar</Button>
                  <Button size="sm" variant="ghost" onClick={() => run(() => api.patch(`/tickets/${t.id}`, { status: "cancelled" }))} data-testid={`ticket-cancel-called-${t.code}`}><X className="h-4 w-4" /></Button>
                  <Button size="sm" onClick={() => run(() => api.patch(`/tickets/${t.id}`, { status: "done" }))} data-testid={`ticket-done-${t.code}`}><Check className="h-4 w-4 mr-1" />Concluir</Button>
                </li>
              ))}
              {!called.length && <li className="p-6 text-center text-sm text-muted-foreground">Nenhuma senha em atendimento.</li>}
            </ul>
          </div>
        </TabsContent>
        <TabsContent value="relatorio" className="mt-4"><Report /></TabsContent>
      </Tabs>
      {cfg && <QueuesDialog open={cfg} onOpenChange={setCfg} />}
    </>
  );
}
